import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { StatusBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus, FileText, Download, Trash2, Settings2, Send, Undo2, Loader2, Clock, Award, Sparkles,
} from "lucide-react";

function CreateExamDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", subject: "", description: "", duration_minutes: 30,
    passing_percentage: 40, attempts_allowed: 1, mode: "common",
    negative_marking: 0, instructions: "",
  });
  const [loading, setLoading] = useState(false);
  const [rewriting, setRewriting] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });

  const rewrite = async (field) => {
    setRewriting(field);
    try {
      const { data } = await api.post("/ai/rewrite", {
        field,
        text: form[field],
        context: `Title: ${form.title || "Untitled"}; Subject: ${form.subject || "General"}`,
      });
      setForm((f) => ({ ...f, [field]: data.text }));
      toast.success("Rewritten with AI");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI rewrite failed");
    } finally {
      setRewriting("");
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/exams", form);
      toast.success("Examination created");
      setOpen(false);
      onCreated?.();
      navigate(`/admin/exams/${data.id}`);
    } catch (e) {
      toast.error("Failed to create examination");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm" data-testid="create-exam-btn"><Plus className="h-4 w-4 mr-1.5" /> Create Examination</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Examination</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2"><Label>Examination Name</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="exam-title" /></div>
            <div className="space-y-1.5"><Label>Subject</Label><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="exam-subject" /></div>
            <div className="space-y-1.5"><Label>Duration (minutes)</Label><Input type="number" min={1} value={form.duration_minutes} onFocus={(e) => e.target.select()} onChange={(e) => set("duration_minutes", +e.target.value)} data-testid="exam-duration" /></div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Description</Label>
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={() => rewrite("description")} disabled={rewriting === "description"} data-testid="rewrite-description">
                {rewriting === "description" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI rewrite
              </Button>
            </div>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Brief description of the exam" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Instructions</Label>
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={() => rewrite("instructions")} disabled={rewriting === "instructions"} data-testid="rewrite-instructions">
                {rewriting === "instructions" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI rewrite
              </Button>
            </div>
            <Textarea value={form.instructions} onChange={(e) => set("instructions", e.target.value)} placeholder="Shown to students before the exam" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Passing %</Label><Input type="number" min={0} value={form.passing_percentage} onFocus={(e) => e.target.select()} onChange={(e) => set("passing_percentage", +e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Attempts allowed</Label><Input type="number" min={1} value={form.attempts_allowed} onFocus={(e) => e.target.select()} onChange={(e) => set("attempts_allowed", +e.target.value)} data-testid="exam-attempts" /></div>
            <div className="space-y-1.5"><Label>Negative marking</Label><Input type="number" step="0.25" min={0} value={form.negative_marking} onFocus={(e) => e.target.select()} onChange={(e) => set("negative_marking", +e.target.value)} /></div>
          </div>
          <p className="text-xs text-muted-foreground">Set "Attempts allowed" above 1 to let students reattempt this exam.</p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading || !form.title || !form.subject} className="rounded-sm" data-testid="submit-exam">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create &amp; add questions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Exams() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/exams");
    setExams(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const publish = async (id, isPub) => {
    try {
      await api.post(`/exams/${id}/${isPub ? "unpublish" : "publish"}`);
      toast.success(isPub ? "Unpublished" : "Published");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/exams/${id}`);
    toast.success("Examination deleted");
    load();
  };

  const downloadReport = async (id, title) => {
    const res = await api.get(`/admin/exams/${id}/report`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${title}-report.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout nav={adminNav} title="Examinations">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Examinations</h1>
          <p className="text-muted-foreground mt-1">Create, build and publish your assessments.</p>
        </div>
        <CreateExamDialog onCreated={load} />
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
      ) : exams.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">No examinations yet. Create your first one.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-border border border-border">
          {exams.map((ex) => (
            <div key={ex.id} className="bg-card p-6 flex flex-col" data-testid={`exam-card-${ex.id}`}>
              <div className="flex items-start justify-between gap-2">
                <StatusBadge status={ex.status} />
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{ex.subject}</span>
              </div>
              <h3 className="mt-3 text-lg font-semibold leading-tight">{ex.title}</h3>
              <div className="text-sm text-muted-foreground">{ex.subject}</div>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono">
                <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {ex.question_count} Q</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {ex.duration_minutes}m</span>
                <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {ex.total_marks} marks</span>
                <span>{ex.attempt_count} attempts</span>
              </div>
              <div className="mt-5 pt-4 border-t border-border flex items-center gap-2">
                <Link to={`/admin/exams/${ex.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full rounded-sm" data-testid={`build-${ex.id}`}><Settings2 className="h-4 w-4 mr-1.5" /> Build</Button>
                </Link>
                <Button size="sm" className="rounded-sm" onClick={() => publish(ex.id, ex.status === "published")} data-testid={`publish-${ex.id}`}>
                  {ex.status === "published" ? <><Undo2 className="h-4 w-4 mr-1.5" /> Unpublish</> : <><Send className="h-4 w-4 mr-1.5" /> Publish</>}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => downloadReport(ex.id, ex.title)} title="Report"><Download className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(ex.id)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
